/**
 * Martha AI Assistant Context
 * Manages state for the "Talk with Martha" AI assistant.
 *
 * Extended in EXL-747 to expose page-context and suggested actions for
 * the new ChatPanel. Conversation/WebSocket state is managed separately
 * in useConversation + WebSocketService.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  metadata?: {
    documentId?: string;
    action?: 'generate_spec' | 'generate_epics' | 'explain' | 'suggest';
  };
}

export interface MarthaContextValue {
  // Chat state
  isOpen: boolean;
  messages: ChatMessage[];
  isLoading: boolean;

  // Actions
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  sendMessage: (content: string) => Promise<void>;
  clearMessages: () => void;

  // Context-aware features
  currentPageContext: string;
  suggestedActions: string[];
}

const MarthaContext = createContext<MarthaContextValue | undefined>(undefined);

// Generate a unique ID for messages
const generateId = () => `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Get context based on current page (uses semantic route segments)
const getPageContext = (pathname: string): string => {
  if (pathname.includes('/sprints')) return 'sprint';
  if (pathname.includes('/boards')) return 'board';
  if (pathname.includes('/epics')) return 'epics';
  if (pathname.includes('/documents')) return 'documentation';
  if (pathname.includes('/monitor')) return 'telemetry';
  if (pathname.includes('/editor')) return 'editor';
  if (pathname.includes('/testing')) return 'testing';
  if (pathname.includes('/coder')) return 'sprites';
  if (pathname.includes('/requirements')) return 'requirements';
  if (pathname.includes('/specifications')) return 'specifications';
  if (pathname.includes('/projects')) return 'projects';
  return 'general';
};

// Dynamic agent-aware hints — surfaces real capabilities for internal Archie team
const CONTEXT_HINTS: Record<string, string[]> = {
  sprint: [
    'Ask Work Agent to check sprint readiness across all epics',
    'Use Work Agent to find unestimated stories in the current sprint',
    'Ask Martha to kick off a sprint build for the current epics',
  ],
  board: [
    'Ask Work Agent to show all blocked issues on this board',
    'Use Work Agent to bulk-create stories from this epic breakdown',
    'Ask Martha to pull the latest Figma frames and attach them to this epic',
  ],
  requirements: [
    'Ask Martha to generate a specification from this requirements doc',
    'Use Writer Agent to draft a spec from the requirements doc',
    'Ask Knowledge Agent to surface any gaps in documentation coverage',
  ],
  specifications: [
    'Ask Martha to score this document for completeness and quality',
    'Use Developer Agent to review the latest PR for architecture issues',
    'Ask Knowledge Agent to find which epics are missing test specs',
  ],
  epics: [
    'Use Work Agent to bulk-create stories from this epic breakdown',
    'Ask Martha to check build readiness — are all specs linked to tests?',
    'Ask Knowledge Agent to map dependencies between our requirements docs',
  ],
  documentation: [
    'Ask Knowledge Agent to find all specs related to the orchestrator',
    'Use Writer Agent to rewrite this section to be more concise',
    'Ask Martha to score this document for completeness and quality',
  ],
  telemetry: [
    'Ask Martha to show recent activity — who changed what today?',
    'Use MTP Eval Agent to compare model accuracy across jurisdictions',
    'Ask MTP Pipeline Agent for the status of the latest fine-tuning run',
  ],
  testing: [
    'Ask Security Agent to scan our dependencies for known vulnerabilities',
    'Use Code Quality Agent to find duplicate code across the monorepo',
    'Ask Developer Agent to generate tests for the auth module',
  ],
  sprites: [
    'Ask Martha to kick off a sprint build for the current epics',
    'Use Developer Agent to audit our dependency tree for outdated packages',
    'Ask Code Quality Agent to generate a complexity report for the orchestrator',
  ],
  projects: [
    'Ask Martha to check build readiness — are all specs linked to tests?',
    'Use Martha to search across all projects for anything about authentication',
    'Ask Knowledge Agent to surface any gaps in documentation coverage',
  ],
  editor: [
    'Use Writer Agent to rewrite this section to be more concise',
    'Ask Writer Agent to proofread and tighten up this document',
    'Ask Martha to generate a specification from this requirements doc',
  ],
};

const DEFAULT_HINTS = [
  'Ask Developer Agent to review the latest PR for architecture issues',
  'Use Knowledge Agent to find all specs related to the orchestrator',
  'Ask Martha to generate a specification from this requirements doc',
  'Use Work Agent to show all blocked issues on this board',
  'Ask Martha to check build readiness — are all specs linked to tests?',
  'Use Content Agent to generate a LinkedIn post about our latest release',
];

const getSuggestedActions = (context: string): string[] => {
  const pool = CONTEXT_HINTS[context] || DEFAULT_HINTS;
  const shuffled = [...pool].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, 3);
};

export const MarthaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const currentPageContext = getPageContext(location.pathname);
  const suggestedActions = getSuggestedActions(currentPageContext);

  const openChat = useCallback(() => setIsOpen(true), []);
  const closeChat = useCallback(() => setIsOpen(false), []);
  const toggleChat = useCallback(() => setIsOpen((prev) => !prev), []);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const sendMessage = useCallback(async (content: string) => {
    // Add user message
    const userMessage: ChatMessage = {
      id: generateId(),
      role: 'user',
      content,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const answer = await askMarthaApi(content, currentPageContext);

      const assistantMessage: ChatMessage = {
        id: generateId(),
        role: 'assistant',
        content: answer,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Failed to send message:', error);
      const errorMessage: ChatMessage = {
        id: generateId(),
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [currentPageContext]);

  const value: MarthaContextValue = {
    isOpen,
    messages,
    isLoading,
    openChat,
    closeChat,
    toggleChat,
    sendMessage,
    clearMessages,
    currentPageContext,
    suggestedActions,
  };

  return <MarthaContext.Provider value={value}>{children}</MarthaContext.Provider>;
};

export const useMartha = (): MarthaContextValue => {
  const context = useContext(MarthaContext);
  if (!context) {
    throw new Error('useMartha must be used within a MarthaProvider');
  }
  return context;
};

/**
 * Call the orchestration /ask endpoint for lightweight Q&A.
 * Derives projectId from the current URL path (/pr/:projectId/...).
 * Falls back to a generic answer if the API is unreachable.
 */
async function askMarthaApi(question: string, pageContext: string): Promise<string> {
  const projectId = extractProjectIdFromPath(window.location.pathname);
  if (!projectId) {
    return `I can help with that once you navigate to a project. Currently in ${pageContext} mode.`;
  }

  const res = await fetch(`/api/orchestration/conversations/${projectId}/ask`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ question }),
  });

  if (!res.ok) throw new Error(`API ${res.status}`);

  const body = (await res.json()) as { data: { answer: string } };
  return body.data.answer;
}

/** Extract the 7-char projectId from URL paths like /pr/:projectId/... */
function extractProjectIdFromPath(pathname: string): string | null {
  const match = pathname.match(/\/pr\/([^/]+)/);
  return match ? match[1] : null;
}
